import React, { useState } from 'react';
import { Rating, TextField, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useForm } from '@formspree/react';
import { ReservationRate, ReservationStudent } from '../../../types/Reservation';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '../../Button';
import { ButtonPaddingSize } from '../../Button/Button.types';
import './RatingForm.css';

interface RatingFormProps {
  existingRate: ReservationRate | null | undefined;
  reservationId: string;
  student: ReservationStudent;
  onSubmit: (stars: number, comment: string) => Promise<void>;
}

const RatingForm: React.FC<RatingFormProps> = ({
  existingRate,
  reservationId,
  student,
  onSubmit
}) => {
  const [stars, setStars] = useState<number | null>(null);
  const [complaint, setComplaint] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [, formspreeSubmit] = useForm('xqkryvln');

  if (existingRate) {
    return (
      <div className="rating-readonly">
        <Rating
          value={existingRate.value + 1}
          readOnly
          size="large"
          sx={{ color: 'var(--primary-blue)' }}
        />
        <Typography variant="body2" sx={{ color: 'var(--gray-500)' }}>
          {existingRate.label}
        </Typography>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rating-submitted">
        <CheckCircleOutlineIcon sx={{ color: 'var(--primary-green)', fontSize: '1.5rem' }} />
        <Typography variant="body1" sx={{ color: 'var(--primary-green)', fontWeight: 600 }}>
          ¡Gracias por tu calificación!
        </Typography>
      </div>
    );
  }

  const showComplaint = stars !== null && stars <= 2;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stars) return;

    setIsLoading(true);

    try {
      if (showComplaint && complaint.trim()) {
        try {
          await formspreeSubmit(e);
        } catch {
          // complaint submission failed silently — rating still goes through
        }
      }

      await onSubmit(stars, complaint);
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rating-form">
      <div>
        <Typography variant="body2" sx={{ color: 'var(--gray-500)', mb: 0.5 }}>
          ¿Cómo estuvo la clase?
        </Typography>
        <Rating
          value={stars}
          onChange={(_e, value) => setStars(value)}
          size="large"
          sx={{ color: 'var(--primary-blue)' }}
        />
      </div>
      {showComplaint && (
        <React.Fragment>
          <TextField
            multiline
            rows={3}
            size="small"
            placeholder="¿Tuviste un problema con tu clase? Contanos..."
            value={complaint}
            onChange={(e) => setComplaint(e.target.value)}
            fullWidth
          />
          <input type="hidden" name="name" value={`${student.first_name} ${student.last_name}`} />
          <input type="hidden" name="_replyto" value={student.email} />
          <input type="hidden" name="telephone" value={student.phone_number} />
          <input
            type="hidden"
            name="complaint"
            value={`Reclamo de mala puntuación en reserva: ${reservationId}\n${complaint}`}
          />
          <input type="hidden" name="_subject" value="Reclamo Estudi" />
        </React.Fragment>
      )}
      <Button
        type="submit"
        variant={ButtonVariant.CONTAINED}
        color={ButtonColor.PRIMARY}
        size={ButtonSize.MD}
        paddingSize={ButtonPaddingSize.MD}>
        {isLoading ? 'Enviando...' : 'Enviar calificación'}
      </Button>
    </form>
  );
};

export default RatingForm;
